import { useState } from "react";
import ReactPaginate from "react-paginate";
import { useQuery, useQueryClient } from "react-query";
import { useAppModal } from "components/common/AppModalProvider";
import { getAdminBookingList, patchCancelBooking } from "apis";

const BookingManageComponent = () => {
    const queryClient = useQueryClient();
    const { showAlert, showConfirm } = useAppModal();
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const { data, isLoading, isError } = useQuery(
        ["adminBookingList", currentPage, itemsPerPage],
        () => getAdminBookingList(currentPage, itemsPerPage),
        { keepPreviousData: true }
    );

    if (isLoading) return <div className="p-8"><div className="animate-pulse h-96 bg-achromatic-100 rounded-2xl" /></div>;
    if (isError) return <div className="p-8 text-red-500">오류가 발생했습니다.</div>;

    const bookingList = (data as any)?.data?.data?.items || [];
    const totalItems = (data as any)?.data?.data?.total || 0;
    const pageCount = Math.ceil(totalItems / itemsPerPage);

    const handlePageClick = ({ selected }: { selected: number }) => setCurrentPage(selected + 1);

    const statusLabel = (status: string) => {
        switch (status) {
            case 'COMPLETED': return { text: '완료', cls: 'bg-emerald-50 text-emerald-600' };
            case 'CANCELLED': return { text: '취소', cls: 'bg-red-50 text-red-600' };
            case 'PAID': return { text: '결제완료', cls: 'bg-blue-50 text-blue-600' };
            default: return { text: '대기', cls: 'bg-amber-50 text-amber-600' };
        }
    };

    const handleCancelBooking = async (bookingId: string) => {
        if (!(await showConfirm('이 예약을 강제 취소하시겠습니까?', {
            title: '예약 강제 취소',
            confirmText: '취소 처리',
            variant: 'warning',
        }))) return;
        try {
            const response = await patchCancelBooking(bookingId);
            if ((response as any).status === 200) {
                showAlert("예약이 취소되었습니다.", { variant: 'success' });
                queryClient.invalidateQueries(["adminBookingList"]);
            }
        } catch (error) {
            showAlert("처리 중 오류가 발생했습니다.", { variant: 'error' });
        }
    };

    return (
        <div className="p-8">
            <div className="mb-6">
                <h1 className="text-2xl font-extrabold text-achromatic-900">예약 관리</h1>
                <p className="text-sm text-achromatic-400 mt-1">전체 {totalItems}건의 예약</p>
            </div>

            <div className="bg-white rounded-2xl border border-achromatic-100 overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-achromatic-100 bg-achromatic-50">
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">#</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">서비스</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">게스트</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">호스트</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">일시</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-right">결제금액</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">상태</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">예약일</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">액션</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bookingList.map((item: any, index: number) => {
                            const st = statusLabel(item.status);
                            return (
                                <tr key={item.id} className="border-b border-achromatic-50 hover:bg-primary-50/30 transition-colors">
                                    <td className="py-3 px-4 text-[13px] text-achromatic-500">{index + 1 + (currentPage - 1) * itemsPerPage}</td>
                                    <td className="py-3 px-4 text-[13px] font-semibold text-achromatic-900 max-w-[180px] truncate">{item.serviceTitle}</td>
                                    <td className="py-3 px-4 text-[13px] text-achromatic-600">{item.guestName}</td>
                                    <td className="py-3 px-4 text-[13px] text-achromatic-600">{item.hostName}</td>
                                    <td className="py-3 px-4 text-[13px] text-achromatic-500 text-center">
                                        {item.scheduleDate} {item.scheduleStartTime}~{item.scheduleEndTime}
                                    </td>
                                    <td className="py-3 px-4 text-[13px] font-bold text-achromatic-900 text-right">
                                        {Number(item.paidAmount).toLocaleString()} <span className="text-[11px] font-medium text-achromatic-400">원</span>
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${st.cls}`}>
                                            {st.text}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-[13px] text-achromatic-500 text-center">
                                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString('ko-KR') : '-'}
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        {item.status !== 'CANCELLED' && item.status !== 'COMPLETED' && (
                                            <button
                                                className="px-3 py-1.5 bg-red-50 text-red-600 text-[11px] font-bold rounded-lg hover:bg-red-100 transition-colors"
                                                onClick={() => handleCancelBooking(item.id)}
                                            >
                                                강제 취소
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                <div className="p-4 border-t border-achromatic-100 flex justify-center">
                    <ReactPaginate
                        previousLabel={"←"} nextLabel={"→"} breakLabel={"..."} pageCount={pageCount}
                        marginPagesDisplayed={1} pageRangeDisplayed={5} onPageChange={handlePageClick}
                        containerClassName={"flex items-center gap-1"}
                        previousClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-500 hover:bg-achromatic-100"}
                        nextClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-500 hover:bg-achromatic-100"}
                        pageClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-600 hover:bg-achromatic-100"}
                        activeClassName={"!bg-primary-500 !text-white"} activeLinkClassName={"text-white"}
                    />
                </div>
            </div>
        </div>
    );
};

export default BookingManageComponent;
